import React, { forwardRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { FixedGrid, type ITheme } from '../../../../../ui';
import { ParkingProductCard } from './card/ParkingProductCard';
import { StyledParkingProductCard } from './card/StyledParkingProductCard';
import { AvailabilityResultsShimmer } from './AvailabilityResultsShimmer';
import { StyledParkingProductCardShimmer } from './card/ParkingProductCardShimmer';
import {
	getOverlapWithHeaderStyles,
	StyledHeader,
} from '../../../../shared/components';
import { IParkingProduct } from '../../../domain/parking-product/models/IParkingProduct';
import { trackViewParkingProducts } from '../shared/analytics/e-commerce/trackViewParkingProducts';
import Responsive from '../../../../../ui/utils/Responsive';
import ContentWrap from '../../../../../ui/components/Layout/ContentWrap';

interface IAvailabilityResultsContentProps {
	loading: boolean;
	promoCode?: string;
	isPromoCodeValid: boolean;
	parkingProducts: IParkingProduct[];
	selectedParkingProductId?: number;
	onSelectProduct: (parkingProduct: IParkingProduct) => void;
}

const StyledAvailabilityResultsContent = styled(ContentWrap)<{}, ITheme>`
	${StyledHeader} + style + &,
	${StyledHeader} + & {
		${getOverlapWithHeaderStyles()}
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		padding-bottom: 24px;
	}
`;

export const AvailabilityResultsContent = forwardRef<
	HTMLDivElement,
	IAvailabilityResultsContentProps
>(
	(
		{
			loading,
			promoCode,
			isPromoCodeValid,
			parkingProducts,
			selectedParkingProductId,
			onSelectProduct,
		}: IAvailabilityResultsContentProps,
		ref,
	) => {
		useEffect(() => {
			if (!loading) {
				trackViewParkingProducts(parkingProducts, promoCode, isPromoCodeValid);
			}
		}, [loading, parkingProducts]);

		const shouldShowResults = loading || parkingProducts.length > 0;

		if (!shouldShowResults) return null;

		return (
			<StyledAvailabilityResultsContent>
				<FixedGrid
					itemSelector={[
						StyledParkingProductCard,
						StyledParkingProductCardShimmer,
					]}
					columns={[3, { tablet: 2, mobile: 1 }]}
					ref={ref}
					role="list"
				>
					{loading ? (
						<AvailabilityResultsShimmer />
					) : (
						parkingProducts.map((parkingProduct) => (
							<ParkingProductCard
								key={parkingProduct.id}
								parkingProduct={parkingProduct}
								selected={selectedParkingProductId === parkingProduct.id}
								onClick={() => onSelectProduct(parkingProduct)}
							/>
						))
					)}
				</FixedGrid>
			</StyledAvailabilityResultsContent>
		);
	},
);
